/**
 * RITM - the request item: a structured operational request that is not an
 * incident (create a tenant, set up a custom domain, provision a number, add
 * an operator, replay a webhook). Its job is to stop these from being Slack
 * messages nobody can find a month later.
 */

export const REQUEST_STATUSES = ['NEW', 'APPROVAL', 'QUEUED', 'IN_PROGRESS', 'WAITING', 'FULFILLED', 'CANCELED', 'REJECTED'] as const;
export type RequestStatus = (typeof REQUEST_STATUSES)[number];

export const REQUEST_TRANSITIONS: Readonly<Record<RequestStatus, readonly RequestStatus[]>> = {
  NEW: ['APPROVAL', 'QUEUED', 'IN_PROGRESS', 'CANCELED', 'REJECTED'],
  APPROVAL: ['QUEUED', 'IN_PROGRESS', 'REJECTED', 'CANCELED'],
  QUEUED: ['IN_PROGRESS', 'WAITING', 'CANCELED'],
  IN_PROGRESS: ['WAITING', 'FULFILLED', 'CANCELED'],
  WAITING: ['IN_PROGRESS', 'FULFILLED', 'CANCELED'],
  FULFILLED: [],
  CANCELED: [],
  REJECTED: [],
};

export function canTransitionRequest(from: RequestStatus, to: RequestStatus): boolean {
  return REQUEST_TRANSITIONS[from].includes(to);
}

export const REQUEST_TERMINAL_STATUSES: readonly RequestStatus[] = ['FULFILLED', 'CANCELED', 'REJECTED'];

export function isRequestOpen(status: RequestStatus): boolean {
  return !REQUEST_TERMINAL_STATUSES.includes(status);
}

export const REQUEST_STATUS_TIMESTAMP: Readonly<Partial<Record<RequestStatus, 'fulfilledAt' | 'closedAt'>>> = {
  FULFILLED: 'fulfilledAt',
  CANCELED: 'closedAt',
  REJECTED: 'closedAt',
};

export const REQUEST_CATEGORIES = ['TENANT', 'AUCTION', 'COMMUNICATIONS', 'ACCESS', 'DATA', 'OTHER'] as const;
export type RequestCategory = (typeof REQUEST_CATEGORIES)[number];

/**
 * The small operational catalog (design doc section 42). Titles only in Phase
 * 1: a catalog item pre-fills a request, it does not yet drive fulfilment.
 */
export const REQUEST_CATALOG: readonly { readonly category: RequestCategory; readonly title: string; readonly items: readonly string[] }[] = [
  { category: 'TENANT', title: 'Tenant', items: ['Create tenant', 'Disable tenant', 'Custom domain', 'Branding update'] },
  { category: 'AUCTION', title: 'Auction', items: ['Auction recovery', 'Auction state correction', 'Enable CheckShield', 'Reopen administrative workflow'] },
  { category: 'COMMUNICATIONS', title: 'Communications', items: ['Provision phone number', 'Configure sender', 'Enable Facebook Page connection', 'Enable YouTube integration'] },
  { category: 'ACCESS', title: 'Access', items: ['Add operator', 'Change role', 'Temporary support access'] },
  { category: 'DATA', title: 'Data', items: ['Export', 'Restore', 'Replay', 'Reconcile'] },
  { category: 'OTHER', title: 'Other', items: [] },
];
